"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import { ThemeToggle } from "./AppearanceProvider";
import { Brand } from "./Brand";
import "./site-header.css";

type IconName = "studio" | "arrow";

function NavIcon({ name }: { name: IconName }) {
  return <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {name === "studio" ? <path d="m12 3 9 5v8l-9 5-9-5V8l9-5ZM3 8l9 5 9-5m-9 5v8M7.5 5.5l9 5"/> : <path d="M5 12h14m-5-5 5 5-5 5"/>}
  </svg>;
}

const groups = [
  { label: "Learn", href: "/learn", sections: ["/lessons", "/glossary"] },
  { label: "Explore", href: "/explore", sections: ["/openings", "/endgames", "/games", "/champions", "/variants"] },
  { label: "Journal", href: "/blog", sections: [] },
] as const;

const isWithin = (pathname: string, href: string) => pathname === href || pathname.startsWith(href + "/");

// Reset disclosure state on navigation, including browser back/forward.
export function SiteHeader() {
  const pathname = usePathname();
  return <HeaderNavigation key={pathname} pathname={pathname} />;
}

function HeaderNavigation({ pathname }: { pathname: string }) {

  const [mobileOpen, setMobileOpen] = useState(false);
  const header = useRef<HTMLElement>(null);
  const mobileTrigger = useRef<HTMLButtonElement>(null);

  const close = () => { setMobileOpen(false); };

  useEffect(() => {
    if (!mobileOpen) return;
    const dismiss = (event: PointerEvent) => {
      if (!header.current?.contains(event.target as Node)) { setMobileOpen(false); }
    };
    const escape = (event: globalThis.KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      mobileTrigger.current?.focus();
      setMobileOpen(false);
    };
    document.addEventListener("pointerdown", dismiss);
    document.addEventListener("keydown", escape);
    return () => { document.removeEventListener("pointerdown", dismiss); document.removeEventListener("keydown", escape); };
  }, [mobileOpen]);

  useEffect(() => {
    const breakpoint = matchMedia("(max-width: 1000px)");
    const reset = () => { setMobileOpen(false); };
    breakpoint.addEventListener("change", reset);
    return () => breakpoint.removeEventListener("change", reset);
  }, []);

  useEffect(() => {
    if (!mobileOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = previous; };
  }, [mobileOpen]);

  return <header ref={header} className="site-header" data-play-route={pathname === "/play" || undefined} onBlur={(event) => {
    if (event.relatedTarget && !event.currentTarget.contains(event.relatedTarget)) close();
  }}>
    <div className="header-inner">
      <Link href="/" className="site-logo" aria-label="Ponderchess home" onClick={close}><Brand /></Link>
      <nav aria-label="Main" className="desktop-nav">
        <Link className="nav-link" href="/play" aria-current={isWithin(pathname, "/play") ? "page" : undefined} onClick={close}>Play</Link>
        {groups.map((group) => <Link key={group.href} className="nav-link" href={group.href} data-active={isWithin(pathname, group.href) || group.sections.some((href) => isWithin(pathname, href)) || undefined} aria-current={pathname === group.href ? "page" : undefined} onClick={close}>{group.label}</Link>)}
        <Link className="nav-link" href="/puzzles" aria-current={isWithin(pathname, "/puzzles") ? "page" : undefined} onClick={close}>Puzzles</Link>
      </nav>
      <div className="header-actions">
        <ThemeToggle />
        <button ref={mobileTrigger} type="button" className="mobile-nav-toggle" aria-label={mobileOpen ? "Close navigation menu" : "Open navigation menu"} aria-expanded={mobileOpen} aria-controls="mobile-navigation" onClick={() => setMobileOpen(!mobileOpen)}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden="true">{mobileOpen ? <path d="m6 6 12 12M6 18 18 6"/> : <path d="M4 7h16M4 12h16M4 17h16"/>}</svg>
        </button>
      </div>
    </div>
    <nav id="mobile-navigation" className="mobile-navigation" aria-label="Mobile" hidden={!mobileOpen}>
      <div className="mobile-quick-links">{[{ href: "/play", label: "Play a game" }, { href: "/puzzles", label: "Solve puzzles" }].map((link) => <Link key={link.href} href={link.href} aria-current={pathname === link.href ? "page" : undefined} onClick={close}>{link.label}<NavIcon name="arrow" /></Link>)}</div>
      <div className="mobile-primary-links">{groups.map((group) => <Link key={group.href} href={group.href} aria-current={pathname === group.href ? "page" : undefined} data-active={isWithin(pathname, group.href) || group.sections.some((href) => isWithin(pathname, href)) || undefined} onClick={close}>{group.label}<NavIcon name="arrow" /></Link>)}</div>
      <div className="mobile-tools">{[{ href: "/about", label: "About Ponderchess" }].map((link) => <Link key={link.href} href={link.href} aria-current={pathname === link.href ? "page" : undefined} onClick={close}>{link.label}<NavIcon name="arrow" /></Link>)}</div>
      <p className="mobile-nav-signoff">Take your time. Find your move.</p>
    </nav>
  </header>;
}

